import type { Writable } from "svelte/store";
import type { FtpConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// The browser cannot speak FTP. File calls go through /api/ftp. The user and
// password are request headers, not query parameters.

class FileSystemFtp extends FS {
  supportsDirRename = true;

  constructor(config: FtpConfig) {
    super(config);
  }

  get ftpConfig(): FtpConfig {
    return this.config as FtpConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await ftpList(this.ftpConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the password header. Fetch through
    // the same-origin proxy and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = ftpURL(this.ftpConfig, "/api/ftp/download", "path", path);
    let xhr = await ftpSend("GET", url, this.ftpConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = ftpURL(this.ftpConfig, "/api/ftp/upload", "path", path);
    await ftpSend("PUT", url, this.ftpConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = ftpURL(this.ftpConfig, "/api/ftp/rename", "path", path, "newPath", newPath);
    await ftpCall(this.ftpConfig, url, "POST");
  }

  async createDirectory(path: string): Promise<void> {
    let url = ftpURL(this.ftpConfig, "/api/ftp/mkdir", "path", path);
    await ftpCall(this.ftpConfig, url, "POST");
  }

  async deleteFile(path: string): Promise<void> {
    let url = ftpURL(this.ftpConfig, "/api/ftp/delete", "path", path);
    await ftpCall(this.ftpConfig, url, "POST");
  }
}

export async function testFtpConnection(config: FtpConfig): Promise<void> {
  await ftpCall(config, ftpURL(config, "/api/ftp/test"));
}

async function ftpList(config: FtpConfig, dir: string): Promise<any[][]> {
  let js = await ftpCall(config, ftpURL(config, "/api/ftp/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("ftp list failed");
  }
  return js;
}

function ftpURL(config: FtpConfig, path: string, ...args: string[]): string {
  return mkURL(path, "host", config.host, "port", config.port, "tls", config.tls, ...args);
}

function encodeSecret(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function ftpHeaders(config: FtpConfig): Record<string, string> {
  let headers: Record<string, string> = {};
  if (config.user) {
    headers["X-Ftp-User"] = encodeSecret(config.user);
  }
  if (config.password) {
    headers["X-Ftp-Password"] = encodeSecret(config.password);
  }
  return headers;
}

async function ftpCall(config: FtpConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, { method, headers: ftpHeaders(config) });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `ftp ${rsp.status}`);
  }
  return js;
}

function ftpSend(
  method: string,
  url: string,
  config: FtpConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  let headers = ftpHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      ftpErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`ftp ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("ftp request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function ftpErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `ftp ${xhr.status}`;
}

export function makeFileSystemFtp(config: FtpConfig): FileSystemFtp {
  return new FileSystemFtp(config);
}
